import { useMemo, useState } from 'react';
import { usePostHog } from '@posthog/react';

import { StatusDropdown } from '@/components/ui/status-dropdown';
import { useUpdateOrderStatus } from '@hooks/orders';
import { useTranslation } from '@hooks/useTranslation';
import { useToast } from '@/hooks/use-toast';
import { getAllErrorReasons } from '@api/client';
import { spineLinkBlocked, type SpineLinkBlocked } from '@/react/utils/spineLinkError';

import { CancelOrderModal } from './CancelOrderModal';
import { ConfirmationDialog } from '../common/ConfirmationDialog';
import { buildSpineBlockedConfirmCopy } from './spine-blocked-copy';
import { useRetainedDialogState } from './useRetainedDialogState';
import { createOrderStatusDropdownConfig, type OrderStatus } from './order-status-config';

interface OrderStatusControlProps {
  orderId: number;
  status: OrderStatus;
}

export function OrderStatusControl({ orderId, status }: OrderStatusControlProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const posthog = usePostHog();
  const updateStatus = useUpdateOrderStatus();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const spineBlockedDialog = useRetainedDialogState<SpineLinkBlocked>();

  const statusConfig = useMemo(
    () =>
      createOrderStatusDropdownConfig({
        lead: t('statuses.lead'),
        confirmed: t('statuses.confirmed'),
        cancelled: t('statuses.cancelled'),
      }),
    [t],
  );

  const handleStatusChange = (newStatus: OrderStatus) => {
    if (newStatus === 'cancelled') {
      setShowCancelModal(true);
      return;
    }
    updateStatus.mutate(
      { id: orderId, status: newStatus },
      {
        onSuccess: () => {
          if (newStatus === 'confirmed') {
            posthog.capture('order_confirmed', { order_id: orderId });
          }
        },
        onError: (error: Error) => {
          const blocked = spineLinkBlocked(error);
          if (blocked) {
            spineBlockedDialog.show(blocked);
            return;
          }
          toast({
            description: getAllErrorReasons(error, t('toast.status_update_failed')),
            variant: 'destructive',
          });
        },
      },
    );
  };

  // The payload stays populated while open changes to false, so Radix can
  // animate the dialog out without replacing its message.
  const spineBlockedCopy = spineBlockedDialog.value
    ? buildSpineBlockedConfirmCopy(spineBlockedDialog.value, t)
    : { title: '', message: '' };

  return (
    <>
      <StatusDropdown value={status} config={statusConfig} onChange={handleStatusChange} />
      <CancelOrderModal
        orderId={orderId}
        open={showCancelModal}
        onOpenChange={setShowCancelModal}
      />
      <ConfirmationDialog
        open={spineBlockedDialog.open}
        onOpenChange={spineBlockedDialog.setOpen}
        onConfirm={() => spineBlockedDialog.setOpen(false)}
        title={spineBlockedCopy.title}
        message={spineBlockedCopy.message}
        hideActions
      />
    </>
  );
}
